<template>
  <div class="booking-open-forms-step">
    <header class="booking-open-forms-step__head">
      <h3>Additional Information</h3>
      <p>Complete the form below to continue with your booking.</p>
    </header>

    <div class="openforms-container">
      <div v-if="!showLoader" :id="containerId" class="booking-open-forms-step__frame"></div>
      <div v-else class="booking-open-forms-step__loader">
        <v-progress-circular indeterminate color="primary" size="48" />
        <p>Processing form submission…</p>
        <p class="booking-open-forms-step__loader-sub">Continuing to review</p>
      </div>
      <div
        v-if="!initialized && !showLoader"
        class="booking-open-forms-step__loader"
      >
        <v-progress-circular indeterminate color="primary" size="48" />
        <p>Loading OpenForms…</p>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "BookingOpenFormsStep",
  props: {
    embededUrl: { type: String, default: "" },
    contactDetails: { type: Object, default: () => ({}) },
  },
  emits: ["submitted"],
  data() {
    return {
      initialized: false,
      showLoader: false,
      containerId: `openforms-form-${Math.random().toString(36).slice(2, 9)}`,
      monitoringItems: [],
      messageHandler: null,
    };
  },
  computed: {
    embedSrc() {
      if (!this.embededUrl) return "";
      try {
        const url = new URL(this.embededUrl);
        const params = url.searchParams;
        const contact = this.contactDetails || {};
        if (contact.FirstName && contact.LastName) {
          params.set("name", `${contact.FirstName} ${contact.LastName}`);
        } else if (contact.FirstName) {
          params.set("name", contact.FirstName);
        }
        if (contact.Email) params.set("email", contact.Email);
        if (contact.Phone) params.set("mobile", contact.Phone);
        const bookingIdVal = this.getFirstVenueBlockId();
        if (bookingIdVal) {
          params.set("bookingId", String(bookingIdVal));
          params.set("bookingid", String(bookingIdVal));
          params.set("Booking-Id", String(bookingIdVal));
        }
        url.search = params.toString();
        return url.toString();
      } catch {
        const q = new URLSearchParams();
        const contact = this.contactDetails || {};
        if (contact.FirstName && contact.LastName) {
          q.set("name", `${contact.FirstName} ${contact.LastName}`);
        } else if (contact.FirstName) {
          q.set("name", contact.FirstName);
        }
        if (contact.Email) q.set("email", contact.Email);
        if (contact.Phone) q.set("mobile", contact.Phone);
        const bookingIdVal = this.getFirstVenueBlockId();
        if (bookingIdVal) {
          q.set("bookingId", String(bookingIdVal));
          q.set("bookingid", String(bookingIdVal));
          q.set("Booking-Id", String(bookingIdVal));
        }
        const sep = this.embededUrl.includes("?") ? "&" : "?";
        return this.embededUrl + (q.toString() ? sep + q.toString() : "");
      }
    },
  },
  beforeUnmount() {
    this.cleanup();
  },
  methods: {
    getFirstVenueBlockId() {
      try {
        const vbRaw = sessionStorage.getItem("venueBlockId");
        if (!vbRaw) return null;
        try {
          const parsed = JSON.parse(vbRaw);
          if (Array.isArray(parsed) && parsed.length > 0) {
            return parsed[0] != null ? String(parsed[0]) : null;
          }
        } catch {
          const m = String(vbRaw).match(/^\s*\[\s*(.*?)\s*\]\s*$/);
          if (m && m[1] != null) {
            return m[1].replace(/^['"`](.*)['"`]$/, "$1");
          }
        }
        return String(vbRaw);
      } catch {
        return null;
      }
    },
    initialize() {
      if (this.initialized || !this.embededUrl) return;
      this.initialized = true;
      this.$nextTick(() => {
        this.createIframe();
        this.bindMessageListener();
      });
    },
    bindMessageListener() {
      if (this.messageHandler) return;
      this.messageHandler = (event) => {
        if (
          event.data &&
          typeof event.data === "string" &&
          event.data.includes("scrollTop")
        ) {
          this.showLoader = true;
          this.cleanupMonitoring();
          this.$emit("submitted");
        }
      };
      window.addEventListener("message", this.messageHandler);
    },
    createIframe() {
      const formContainer = document.getElementById(this.containerId);
      if (!formContainer) return;

      formContainer.innerHTML = "";
      const iframe = document.createElement("iframe");
      iframe.src = this.embedSrc;
      iframe.style.border = "0";
      iframe.style.width = "100%";
      iframe.style.minHeight = "800px";
      iframe.style.height = "auto";
      iframe.sandbox = "allow-forms allow-scripts allow-same-origin";
      iframe.scrolling = "auto";
      iframe.name = "openforms-iframe";
      iframe.id = "openforms-iframe-wizard";

      iframe.onload = () => {
        this.sendEmbedPrefill(iframe);
        this.setupDynamicHeight(iframe);
      };

      formContainer.appendChild(iframe);
    },
    sendEmbedPrefill(iframe) {
      try {
        if (!iframe?.contentWindow) return;
        const contact = this.contactDetails || {};
        const payload = {
          type: "embed-prefill",
          bookingId: this.getFirstVenueBlockId(),
          name:
            (contact.FirstName || "") +
            (contact.LastName ? " " + contact.LastName : ""),
          email: contact.Email || "",
          mobile: contact.Phone || "",
        };
        iframe.contentWindow.postMessage(payload, "*");
      } catch {
        // silent
      }
    },
    setupDynamicHeight(iframe) {
      const adjustHeight = () => {
        try {
          if (iframe.contentWindow?.document) {
            const contentHeight =
              iframe.contentWindow.document.body.scrollHeight;
            iframe.style.height = `${Math.max(600, contentHeight + 20)}px`;
          }
        } catch {
          iframe.style.height = `${Math.max(600, window.innerHeight - 200)}px`;
        }
      };
      adjustHeight();
      const resizeHandler = () => adjustHeight();
      window.addEventListener("resize", resizeHandler);
      this.monitoringItems.push({
        type: "resizeListener",
        handler: resizeHandler,
      });
    },
    cleanupMonitoring() {
      this.monitoringItems.forEach((item) => {
        if (item.type === "resizeListener" && item.handler) {
          window.removeEventListener("resize", item.handler);
        }
      });
      this.monitoringItems = [];
    },
    cleanup() {
      this.cleanupMonitoring();
      if (this.messageHandler) {
        window.removeEventListener("message", this.messageHandler);
        this.messageHandler = null;
      }
    },
  },
};
</script>

<style scoped>
.booking-open-forms-step__head h3 {
  margin: 0 0 0.35rem;
  font-size: 1.125rem;
}

.booking-open-forms-step__head p {
  margin: 0 0 1rem;
  color: #5f6368;
}

.openforms-container {
  padding: 20px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fafafa;
  overflow: visible;
}

.booking-open-forms-step__loader {
  text-align: center;
  padding: 2rem 1rem;
}

.booking-open-forms-step__loader-sub {
  font-size: 0.875rem;
  color: #757575;
  margin-top: 0.25rem;
}
</style>
